import { useEffect, useMemo, useRef, useState } from "react";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Button, TextInput } from "components";
import closeImg from "assets/icons/icon-close.svg";

import "./LaboratoryPopup.scss";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { publicRequest } from "utils";

import LaboratoryMap from "views/public/Lab/LaboratoryMap/LaboratoryMap";
import { useLaboratoryMap } from "../LaboratoryMap/useLaboratoryMap";
import { createMap } from "utils/googleMap/createMap";

interface LaboratoryPopupProps {
  isOpen: boolean;
  closePopup: () => void;
}

export type LaboratoryMapInputs = {
  name: string;
  phone: string;
  email: string;
  address: string;
};

export function LaboratoryPopup({ isOpen, closePopup }: LaboratoryPopupProps) {
  const { formatMessage } = useIntl();

  const contentRef = useRef(null);
  const [open, setOpen] = useState(isOpen);
  const [isSent, setIsSent] = useState(false);
  useEffect(() => {
    setOpen(isOpen);
    if (isOpen) {
      setIsSent(false);
    }
  }, [isOpen]);

  const closeOnClickingOutside = (e: any) => {
    if (e?.target === contentRef?.current) {
      closePopup();
    }
  };

  const {
    reset,
    formState: { errors },
    register,
    handleSubmit,
    setValue,
  } = useForm<LaboratoryMapInputs>({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      address: "",
    },
  });
  const formData = new FormData();
  const labCall: SubmitHandler<LaboratoryMapInputs> = async (data, e) => {
    e?.preventDefault();
    for (const [key, value] of Object.entries(data)) {
      value && formData.append(key, value);
    }
    handleLabCall.mutate(formData);
  };

  const handleLabCall = useMutation(
    (data: FormData) =>
      publicRequest({
        url: `/api/email/labcall`,
        method: "POST",
        body: data,
      }),
    {
      onSuccess: () => {
        reset();
        setIsSent(true);
      },
    }
  );

  const [map, setMap] = useState<google.maps.Map>();

  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    map?.setCenter({ lat: 41.7151, lng: 44.8271 });
  }, [map]);

  useEffect(() => {
    createMap({
      map,
      setMap,
      element: mapRef.current,
      mapOptions: {
        center: { lat: 41.7151, lng: 44.8271 },
        zoom: 10,
      },
    });
  }, [map]);

  const { handleAddressInput } = useLaboratoryMap({
    setAddress: setValue,
    map,
  });

  return (
    <div
      ref={contentRef}
      className="LaboratoryPopup"
      onClick={(e) => closeOnClickingOutside(e)}
      style={{ display: open ? "flex" : "none" }}
    >
      <form
        className="LaboratoryPopup_content"
        onSubmit={handleSubmit(labCall)}
      >
        <div className="LaboratoryPopup_header LaboratoryPopup_row">
          <h3> {formatMessage({
            id: "laboratory.call",
            defaultMessage: "ᲚᲐᲑᲝᲠᲐᲢᲝᲠᲘᲘᲡ ᲒᲐᲛᲝᲫᲐᲮᲔᲑᲐ",
          })}</h3>
          <div>
            <img
              src={closeImg}
              alt="closing icon"
              className="LaboratoryPopup_closeBtn"
              onClick={() => {
                closePopup();
              }}
              loading="lazy"
            />
          </div>
        </div>
        {isSent ? (
          <>
            <p className="LaboratoryPopup_message LaboratoryPopup_message--success">
              {formatMessage({
                id: "laboratory.visit.success",
                defaultMessage:
                  "თქვენი მონაცემები წარმატებით გაიგზავნა, ოპერატორი მალე დაგიკავშირდებათ",
              })}
            </p>
            <div className="LaboratoryPopup_buttons">
              <Button
                type="button"
                className="LaboratoryPopup_activeBtn"
                onClick={() => {
                  closePopup();
                }}
              >
                {formatMessage({
                  id: "laboratory.visit.close",
                  defaultMessage: "დახურვა",
                })}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="LaboratoryPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "contact-audit.input-placeholder.name-surname",
                  defaultMessage: "_name-surname_",
                })}
                {...register("name", { required: "Name is required" })}
                error={errors?.name && true}
                errorMessage={errors.name?.message}
              />
            </div>
            <div className="LaboratoryPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "contact-audit.input-placeholder.phone",
                  defaultMessage: "_phone_",
                })}
                {...register("phone", { required: "Phone is required" })}
                error={errors?.phone && true}
                errorMessage={errors.phone?.message}
              />
            </div>
            <div className="LaboratoryPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "contact-audit.input-placeholder.address",
                  defaultMessage: "_address_",
                })}
                {...register("address", {
                  required: "The address is required",
                })}
                error={errors?.address && true}
                errorMessage={errors.address?.message}
                onChange={(e) => {
                  handleAddressInput(e.target.value);
                }}
                address
              />
            </div>
            <div className="info-map">
              <LaboratoryMap mapRef={mapRef} />
            </div>
            <div className="LaboratoryPopup_row">
              <TextInput
                placeholder={formatMessage({
                  id: "contact-audit.input-placeholder.email",
                  defaultMessage: "_email_",
                })}
                type="email"
                {...register("email", { required: "Email is required" })}
                error={errors?.email && true}
                errorMessage={errors.email?.message}
              />
            </div>

            {handleLabCall.isError && (
              <p className="LaboratoryPopup_message LaboratoryPopup_message--error">
                {formatMessage({
                  id: "laboratory.visit.error",
                  defaultMessage:
                    "მონაცემების გაგზავნა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა",
                })}
              </p>
            )}

            <div className="LaboratoryPopup_buttons ">
              <Button
                type="button"
                className="LaboratoryPopup_inActiveBtn"
                onClick={() => {
                  closePopup();
                }}
              >
                  {formatMessage({
                id: "laboratory.back",
                defaultMessage: "უკან",
              })}
              </Button>
              <Button
                type="submit"
                className="LaboratoryPopup_activeBtn"
                disabled={handleLabCall.isLoading}
              >
                {formatMessage({
                id: "laboratory.reserve",
                defaultMessage: "დაჯავშნა",
              })}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
